/**
 * LARCIN — Scent Finder (assets/larcin-scent-finder.js)
 *
 * ONE ENGINE, THREE ROUTES
 * ------------------------
 * Every route ends in the same place. Answers become a preference profile,
 * the profile is scored against every eligible fragrance with the same rules,
 * and the three results are chosen from that ranking. Nothing in this file
 * names a fragrance. The catalogue arrives from Liquid as JSON, built from
 * product metafields, so a fragrance added tomorrow is scored exactly like
 * the ones here today.
 *
 *   answers ──► profile ──► score every product ──► rank ──► pick a set of 3
 *
 * THE PROFILE
 *   accords    weighted scent directions (citrus, vanilla, oud, …) plus a few
 *              literal notes (note:apple), matched against each product's
 *              olfactory families, fragrance family and note text
 *   traits     weighted impressions, matched to custom.quiz_traits
 *   occasions  weighted, matched to custom.quiz_occasions (or the standard
 *              occasion field when those are empty)
 *   axes       targets on the four 1-5 spectra, matched to the product's
 *   strength   a target on 1-5, matched to custom.quiz_strength
 *   reference  a product the customer named, or a brand
 *
 * THE SCORE
 * Each component returns 0..1, or null when the product has no data for it.
 * The total is the weighted mean of the components that returned a number,
 * so a missing field removes that component rather than scoring it as zero.
 * A product missing most of its data takes a small penalty so that, all else
 * equal, a fully described fragrance wins the tie.
 *
 * THE SET
 * Match #1 is the best score. #2 and #3 are the best scores left once each
 * candidate's similarity to what is already picked is subtracted, but only
 * among candidates close to the top score. Quality first, then range.
 *
 * THE CART
 * All three variants go to /cart/add.js in one request. Availability is
 * re-checked first against the live product JSON. The existing LARCIN drawer
 * opens itself on any successful cart write, and the automatic discounts in
 * Shopify decide the price. The percentage shown here is display only.
 */

import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/* ========================================================================
   TAXONOMY
   The only vocabulary the engine knows. Product data is normalised into
   these handles, so "Warm", "warm " and "WARM" are one value.
   ======================================================================== */

const handle = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const TRAITS = {
  clean: 'clean', fresh: 'fresh', polished: 'polished', warm: 'warm', addictive: 'addictive',
  smooth: 'smooth', soft: 'soft', quiet: 'quiet', romantic: 'romantic', playful: 'playful',
  dark: 'dark', bold: 'bold', statement: 'statement', versatile: 'versatile', different: 'different',
};

const OCCASIONS = {
  everyday: 'everyday', work: 'work', university: 'university', date: 'dates',
  evening: 'evenings', 'night-out': 'nights out', 'special-occasion': 'special occasions', formal: 'formal events',
};

/* The standard Shopify occasion field, for products whose quiz occasions
   have not been filled in yet. */
const STANDARD_OCCASIONS = {
  everyday: ['everyday'],
  casual: ['everyday', 'university'],
  formal: ['formal', 'work'],
  'special-occasion': ['special-occasion', 'evening'],
};

/* Accords. `words` are matched against the note text, longest first, so
   "orange blossom" is floral and never also citrus. `families` are the
   olfactory-family and fragrance-family handles that imply the accord. */
const ACCORDS = {
  citrus: { label: 'citrus', families: ['citrus'], words: ['bergamot', 'lemon', 'grapefruit', 'mandarin', 'citron', 'citrus', 'lime', 'petitgrain', 'yuzu', 'orange'] },
  fresh: { label: 'freshness', families: ['fresh'], words: ['fresh notes', 'ozonic', 'mint', 'green tea'] },
  aquatic: { label: 'sea air', families: ['aquatic', 'marine'], words: ['aquatic', 'marine', 'sea salt', 'salt'] },
  aromatic: { label: 'aromatic herbs', families: ['aromatic'], words: ['lavender', 'lavandin', 'sage', 'clary sage', 'rosemary', 'basil', 'geranium', 'juniper', 'oregano'] },
  green: { label: 'green notes', families: ['green', 'green-1'], words: ['green notes', 'fig leaf', 'fig', 'tea', 'violet leaf', 'pine'] },
  fruity: { label: 'fruit', families: ['fruity'], words: ['apple', 'pear', 'pineapple', 'black currant', 'blackcurrant', 'lychee', 'plum', 'mango', 'berries', 'raspberry', 'apricot', 'dried fruits', 'sapodilla'] },
  floral: { label: 'florals', families: ['floral'], words: ['orange blossom', 'orange flower', 'rose', 'jasmine', 'tuberose', 'magnolia', 'lily', 'ylang', 'mimosa', 'neroli', 'plum blossom', 'lily-of-the-valley'] },
  powdery: { label: 'soft powder', families: ['powdery'], words: ['iris', 'orris', 'violet', 'powder', 'aldehydes', 'heliotrope', 'rice'] },
  clean: { label: 'clean musk', families: ['clean'], words: ['white musk', 'aldehydes', 'rice steam', 'cotton', 'soap'] },
  musk: { label: 'musk', families: ['musky'], words: ['musk', 'ambrette', 'cashmeran'] },
  woods: { label: 'woods', families: ['woody'], words: ['sandalwood', 'cedar', 'cedarwood', 'vetiver', 'guaiac', 'birch', 'oak', 'woody notes', 'woods', 'papyrus', 'akigalawood', 'georgywood', 'cashmere wood', 'amberwood', 'patchouli', 'cypriol'] },
  amber: { label: 'amber', families: ['amber', 'oriental'], words: ['amber', 'ambergris', 'ambroxan', 'ambrofix', 'labdanum', 'benzoin', 'resin', 'balsam', 'tolu', 'opoponax', 'cistus', 'styrax'] },
  vanilla: { label: 'vanilla', families: [], words: ['vanilla', 'tonka', 'coumarin'] },
  gourmand: { label: 'sweetness', families: ['gourmand', 'sweet'], words: ['sugar', 'praline', 'caramel', 'coffee', 'cacao', 'chocolate', 'almond', 'coconut', 'milk', 'chestnut', 'rum', 'cognac', 'honey', 'candied'] },
  spice: { label: 'spice', families: ['spicy'], words: ['pink pepper', 'pepper', 'cardamom', 'cinnamon', 'saffron', 'clove', 'ginger', 'nutmeg', 'allspice', 'pimento', 'spicy notes', 'spices', 'spice', 'elemi'] },
  oud: { label: 'oud', families: ['oud'], words: ['oud', 'agarwood'] },
  smoke: { label: 'smoke and incense', families: ['smoky'], words: ['incense', 'smoke', 'smoky', 'frankincense', 'olibanum', 'myrrh', 'cade'] },
  leather: { label: 'leather', families: ['leather'], words: ['leather', 'suede'] },
  tobacco: { label: 'tobacco', families: ['tobacco'], words: ['tobacco'] },
  earthy: { label: 'earthy depth', families: ['earthy'], words: ['earthy', 'moss', 'oakmoss', 'carrot'] },
  dark: { label: 'darker notes', families: ['dark'], words: [] },
};

const ACCORD_WORDS = Object.entries(ACCORDS)
  .flatMap(([key, a]) => a.words.map((word) => [word, key]))
  .sort((a, b) => b[0].length - a[0].length);

const AXES = ['fw', 'cs', 'ss', 'sd'];
const AXIS_WORDS = {
  fw: ['fresher', 'warmer'],
  cs: ['cleaner', 'sweeter'],
  ss: ['smoother', 'spicier'],
  sd: ['easier to wear', 'more unusual'],
};

/* ========================================================================
   QUESTIONS
   Each option carries a signal. Signals only ever point at the taxonomy
   above, never at a product.

   Component weights and question weights are the two dials:
     WEIGHTS  how much each kind of evidence counts in the final score
     weight   how loudly one question speaks inside its kind of evidence
   ======================================================================== */

const WEIGHTS = {
  accords: 3,
  reference: 3,
  axes: 2.5,
  traits: 2,
  occasions: 1.5,
  strength: 1.5,
};

const SCENE_LEANS = {
  clean: { traits: { clean: 1, quiet: 0.6 }, accords: { clean: 0.8, musk: 0.7, powdery: 0.3 }, axes: { cs: 1.5 } },
  woody: { traits: { polished: 0.8, dark: 0.3 }, accords: { woods: 0.9, leather: 0.6, tobacco: 0.3 }, axes: { fw: 3.8 } },
  fresh: { traits: { fresh: 1 }, accords: { citrus: 0.9, aquatic: 0.7, fruity: 0.3 }, axes: { fw: 1.5 } },
  sweet: { traits: { warm: 1, addictive: 0.6 }, accords: { vanilla: 0.8, gourmand: 0.8, spice: 0.5 }, axes: { fw: 4.5, cs: 4.5 } },
  green: { traits: { different: 0.8, fresh: 0.5 }, accords: { green: 0.9, woods: 0.6, earthy: 0.6 }, axes: { sd: 3.8 } },
  night: { traits: { statement: 0.9, bold: 0.7, dark: 0.4 }, accords: { amber: 0.7, oud: 0.5, spice: 0.4 }, axes: { fw: 4.2 }, strength: 4.3 },
  floral: { traits: { romantic: 1, soft: 0.6 }, accords: { floral: 0.9, fruity: 0.4 } },
  spice: { traits: { warm: 0.6, bold: 0.4 }, accords: { spice: 0.9, amber: 0.4 }, axes: { ss: 4.5 } },
};

const opt = (value, label, icon, text, signal) => ({ value, label, icon, text, signal });

const DIRECTION_OPTIONS = {
  citrus: opt('citrus', 'Citrus & aromatic freshness', '◌', 'Bright and energising.', { accords: { citrus: 1, aromatic: 0.8, fresh: 0.5 }, axes: { fw: 1.5 } }),
  apple: opt('apple', 'Apple, lavender & smooth woods', '◍', 'Polished with warmth underneath.', { accords: { 'note:apple': 1, 'note:lavender': 1, aromatic: 0.6, woods: 0.7, vanilla: 0.3 }, axes: { fw: 3.5 } }),
  vanilla: opt('vanilla', 'Vanilla & warm spice', '◐', 'Smooth, sweet and comforting.', { accords: { vanilla: 1, spice: 0.7, gourmand: 0.5 }, axes: { cs: 4, fw: 4.5 } }),
  oud: opt('oud', 'Amber & oud', '●', 'Dark, rich and powerful.', { accords: { amber: 1, oud: 1, smoke: 0.4 }, axes: { fw: 5 } }),
  musk: opt('musk', 'Musk & clean woods', '○', 'Soft, polished and understated.', { accords: { musk: 1, woods: 0.7, clean: 0.6 }, axes: { cs: 1.5 } }),
  dark: opt('dark', 'Tobacco, leather & darker woods', '◆', 'Drier, moodier and more mature.', { accords: { tobacco: 1, leather: 1, woods: 0.6, smoke: 0.5 }, axes: { fw: 4.5, cs: 2 } }),
};

const QUESTIONS = {
  impression: {
    type: 'options',
    weight: 1,
    options: [
      opt('clean', 'Clean & put together', '⌁', 'Polished without trying too hard.', { traits: { clean: 1, polished: 0.5 }, accords: { clean: 0.8, musk: 0.6, fresh: 0.4 }, axes: { cs: 1.5, fw: 2 } }),
      opt('polished', 'Expensive & polished', '◆', 'Refined, confident and considered.', { traits: { polished: 1, statement: 0.3 }, accords: { woods: 0.7, amber: 0.4, powdery: 0.3 }, axes: { sd: 2.5 } }),
      opt('warm', 'Warm & addictive', '◐', 'Inviting, smooth and memorable.', { traits: { warm: 1, addictive: 1 }, accords: { vanilla: 0.8, amber: 0.7, gourmand: 0.6 }, axes: { fw: 4.5, cs: 4 } }),
      opt('dark', 'Dark & mysterious', '●', 'Deeper, moodier and more dramatic.', { traits: { dark: 1, bold: 0.5 }, accords: { oud: 0.8, smoke: 0.7, leather: 0.6, tobacco: 0.5 }, axes: { fw: 4.5, sd: 4 }, strength: 4.5 }),
      opt('fresh', 'Fresh & effortless', '≈', 'Bright, easy and never heavy.', { traits: { fresh: 1, versatile: 0.5 }, accords: { citrus: 0.9, fresh: 0.8, aromatic: 0.4, aquatic: 0.4 }, axes: { fw: 1.5 } }),
    ],
  },
  scene: { type: 'scenes', weight: 0.5 },
  occasion: {
    type: 'options',
    weight: 1,
    options: [
      opt('everyday', 'Everyday', '○', 'Something easy to reach for.', { occasions: { everyday: 1 }, traits: { versatile: 0.6 }, strength: 3 }),
      opt('work', 'Work / university', '□', 'Presentable, clean and controlled.', { occasions: { work: 1, university: 1 }, traits: { polished: 0.5, clean: 0.4, quiet: 0.3 }, strength: 2.8 }),
      opt('date', 'Dates', '♥', 'Close-up, warm and memorable.', { occasions: { date: 1 }, traits: { romantic: 0.5, warm: 0.4, addictive: 0.4 } }),
      opt('night', 'Nights out', '◆', 'More presence and projection.', { occasions: { 'night-out': 1, evening: 0.7 }, traits: { bold: 0.5, statement: 0.3 }, strength: 4 }),
      opt('special', 'Special occasions', '✦', 'Something that feels distinctive.', { occasions: { 'special-occasion': 1, formal: 0.6 }, traits: { statement: 0.6, polished: 0.3 } }),
      opt('versatile', 'One fragrance for everything', '◎', 'Maximum flexibility.', { occasions: { everyday: 0.8, work: 0.4, date: 0.4 }, traits: { versatile: 1 }, axes: { sd: 1.5 } }),
    ],
  },
  strength: { type: 'strength', weight: 1 },
  balance: {
    type: 'scales',
    weight: 1,
    scales: [
      { axis: 'fw', low: 'Fresh', high: 'Warm' },
      { axis: 'cs', low: 'Clean', high: 'Sweet' },
      { axis: 'ss', low: 'Smooth', high: 'Spicy' },
      { axis: 'sd', low: 'Safe', high: 'Different' },
    ],
  },
  direction: { type: 'options', weight: 1.3, options: Object.values(DIRECTION_OPTIONS) },
  known: { type: 'known', weight: 1, optional: true },

  family: {
    type: 'options',
    weight: 1.2,
    options: [
      opt('fresh', 'Fresh & clean', '≈', 'Bright, easy and crisp.', { accords: { fresh: 1, citrus: 0.7, clean: 0.6 }, traits: { fresh: 1, clean: 0.7 }, axes: { fw: 1.5, cs: 1.8 } }),
      opt('warm', 'Warm & sweet', '◐', 'Smooth, cosy and inviting.', { accords: { vanilla: 0.9, gourmand: 0.8, amber: 0.6 }, traits: { warm: 1, addictive: 0.6 }, axes: { fw: 4.5, cs: 4.3 } }),
      opt('woody', 'Woody & polished', '◆', 'Refined, versatile and structured.', { accords: { woods: 1, musk: 0.4 }, traits: { polished: 1, versatile: 0.5 }, axes: { fw: 3.2, cs: 2.2 } }),
      opt('dark', 'Dark & bold', '●', 'Rich, stronger and more dramatic.', { accords: { oud: 0.8, leather: 0.7, tobacco: 0.6, smoke: 0.6, amber: 0.5 }, traits: { dark: 1, bold: 0.8 }, axes: { fw: 4.8, sd: 4 }, strength: 4.5 }),
    ],
  },
  occasionTaste: {
    type: 'options',
    weight: 1,
    options: [
      opt('everyday', 'Everyday', '○', 'Easy to reach for.', { occasions: { everyday: 1 }, traits: { versatile: 0.6 }, strength: 3 }),
      opt('work', 'Work', '□', 'Controlled and clean.', { occasions: { work: 1 }, traits: { polished: 0.5, clean: 0.4 }, strength: 2.8 }),
      opt('date', 'Dates', '♥', 'Warm and attractive.', { occasions: { date: 1 }, traits: { romantic: 0.5, warm: 0.4 } }),
      opt('evening', 'Evenings & events', '✦', 'More presence.', { occasions: { evening: 1, 'special-occasion': 0.7, 'night-out': 0.6 }, traits: { statement: 0.6 }, strength: 4 }),
    ],
  },
  final: {
    type: 'options',
    weight: 1,
    options: [
      opt('citrus', 'Citrus & aromatic', '◌', 'Fresh and lively.', DIRECTION_OPTIONS.citrus.signal),
      opt('vanilla', 'Vanilla & spice', '◐', 'Warm and smooth.', DIRECTION_OPTIONS.vanilla.signal),
      opt('musk', 'Musk & clean woods', '○', 'Quiet and polished.', DIRECTION_OPTIONS.musk.signal),
      opt('oud', 'Amber & oud', '●', 'Dark and powerful.', DIRECTION_OPTIONS.oud.signal),
    ],
  },
  knownRequired: { type: 'known', weight: 1, optional: false },
};

const ROUTES = {
  surprise: ['impression', 'scene', 'occasion', 'strength', 'balance', 'direction', 'known'],
  taste: ['family', 'occasionTaste', 'strength', 'final'],
  known: ['knownRequired'],
};

const STRENGTH_WORDS = ['', 'Very subtle', 'Close & controlled', 'Balanced presence', 'Noticeable', 'Full statement'];

/* ========================================================================
   SMALL HELPERS
   ======================================================================== */

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const clampNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 1 && n <= 5 ? n : null;
};

const list = (value) => (Array.isArray(value) ? value.filter((v) => typeof v === 'string' && v.trim()) : []);

const joinWords = (words) => {
  const w = words.filter(Boolean);
  if (w.length <= 1) return w[0] || '';
  return `${w.slice(0, -1).join(', ')} and ${w[w.length - 1]}`;
};

const capitalise = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

const jaccard = (a, b) => {
  if (!a.size && !b.size) return 0;
  let shared = 0;
  for (const x of a) if (b.has(x)) shared += 1;
  return shared / (a.size + b.size - shared);
};

/* Token search, forgiving of accents, punctuation, word order and the odd
   typo. "Parfums de Marly Layton" and "layton" both land on Layton. */
const STOP = new Set(['by', 'de', 'la', 'le', 'the', 'eau', 'parfum', 'parfums', 'perfume', 'edp', 'edt', 'extrait', 'inspired', 'of', 'and']);
const tokens = (text) =>
  handle(text)
    .split('-')
    .filter((t) => t.length > 1 && !STOP.has(t));

const nearlyEqual = (a, b) => {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1 || Math.min(a.length, b.length) < 4) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (a.length > b.length) i += 1;
    else if (b.length > a.length) j += 1;
    else {
      i += 1;
      j += 1;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
};

const tokenHit = (needle, hay) => hay.some((t) => t === needle || (needle.length >= 3 && t.startsWith(needle)) || nearlyEqual(needle, t));

/* ========================================================================
   PRODUCT NORMALISATION
   Raw metafield values in, a predictable shape out. Anything missing
   becomes an empty set or null, and the engine skips it.
   ======================================================================== */

function normaliseProduct(raw, index) {
  const noteText = list(raw.notes).join(', ');
  const cleanNotes = noteText
    .replace(/\([^)]*\)/g, ' ')
    .split(/[,/]| and /i)
    .map((n) => n.trim().replace(/\s+/g, ' '))
    .filter((n) => n.length > 1);

  const hay = ` ${handle(noteText).replace(/-/g, ' ')} `;
  const accords = new Set();
  let scan = hay;
  for (const [word, key] of ACCORD_WORDS) {
    const needle = ` ${word} `;
    if (scan.includes(needle)) {
      accords.add(key);
      scan = scan.split(needle).join(' ');
    }
  }

  // Accords named by a family field are the fragrance's character. Accords
  // found only in the note list may be a trace. Copy that describes the
  // fragrance only ever quotes the first kind.
  const familyAccords = new Set();
  const familyHandles = [...list(raw.olfactory).map(handle), handle(raw.family)].filter(Boolean);
  for (const fam of familyHandles) {
    for (const [key, accord] of Object.entries(ACCORDS)) {
      if (accord.families.includes(fam)) {
        accords.add(key);
        familyAccords.add(key);
      }
    }
  }

  const traits = new Set(list(raw.traits).map(handle).filter((t) => t in TRAITS));

  let occasions = new Set(list(raw.occasions).map(handle).filter((o) => o in OCCASIONS));
  if (!occasions.size) {
    occasions = new Set(list(raw.stdOccasions).flatMap((o) => STANDARD_OCCASIONS[handle(o)] || []));
  }

  // One note from the top, the heart and the base reads better than three
  // from the opening.
  const perLayer = list(raw.notes)
    .map((layer) => layer.replace(/\([^)]*\)/g, ' ').split(',')[0]?.trim())
    .filter((n) => n && n.length > 1);
  const keyNotes = [...new Set(perLayer.length >= 2 ? perLayer : cleanNotes)].slice(0, 3);

  return {
    index,
    id: raw.id,
    handle: raw.handle,
    title: raw.title,
    url: raw.url,
    image: raw.image,
    variant: raw.variant,
    price: Number(raw.price) || 0,
    available: raw.available === true,
    inspired: typeof raw.inspired === 'string' ? raw.inspired.trim() : '',
    brand: typeof raw.brand === 'string' ? raw.brand.trim() : '',
    noteText: hay,
    keyNotes,
    accords,
    familyAccords,
    traits,
    occasions,
    strength: clampNumber(raw.strength),
    axes: { fw: clampNumber(raw.fw), cs: clampNumber(raw.cs), ss: clampNumber(raw.ss), sd: clampNumber(raw.sd) },
    desc: typeof raw.desc === 'string' ? raw.desc.trim() : '',
    nameTokens: tokens(`${raw.inspired || ''} ${list(raw.aliases).join(' ')}`),
    brandTokens: tokens(raw.brand || ''),
  };
}

/* ========================================================================
   THE ENGINE
   ======================================================================== */

function emptyProfile() {
  return {
    accords: {},
    traits: {},
    occasions: {},
    axes: { fw: [], cs: [], ss: [], sd: [] },
    strength: [],
    reference: null,
  };
}

function addSignal(profile, signal, weight) {
  if (!signal) return;
  for (const kind of ['accords', 'traits', 'occasions']) {
    for (const [key, value] of Object.entries(signal[kind] || {})) {
      profile[kind][key] = (profile[kind][key] || 0) + value * weight;
    }
  }
  // Axis and strength hints from an option are soft. An explicit slider
  // answer carries four times the pull.
  for (const [axis, target] of Object.entries(signal.axes || {})) {
    profile.axes[axis]?.push([target, 0.5 * weight]);
  }
  if (typeof signal.strength === 'number') profile.strength.push([signal.strength, 0.5 * weight]);
}

const weightedMean = (pairs) => {
  const total = pairs.reduce((sum, [, w]) => sum + w, 0);
  return total ? { value: pairs.reduce((sum, [v, w]) => sum + v * w, 0) / total, evidence: total } : null;
};

function hasAccord(product, key) {
  if (key.startsWith('note:')) return product.noteText.includes(` ${key.slice(5)} `);
  return product.accords.has(key);
}

/**
 * How well a product's tags answer what the customer asked for.
 *
 * Recall alone rewards a product for listing more tags: tick every trait and
 * every profile is fully covered. So recall is blended with precision, the
 * share of the product's own tags the customer actually asked about. A
 * fragrance described with two accurate traits then competes fairly with one
 * described with five, and tag-stuffing stops paying.
 *
 * `recallShare` sets the blend per kind of evidence. Accords lean on recall,
 * because a complex fragrance legitimately carries many.
 */
function overlap(wanted, has, own, recallShare) {
  let total = 0;
  let hit = 0;
  for (const [key, weight] of Object.entries(wanted)) {
    if (weight <= 0) continue;
    total += weight;
    if (has(key)) hit += weight;
  }
  if (!total) return null;
  const recall = hit / total;
  if (!own?.size) return recall;
  let relevant = 0;
  for (const key of own) if ((wanted[key] || 0) > 0) relevant += 1;
  const precision = relevant / own.size;
  return recallShare * recall + (1 - recallShare) * precision;
}

function similarity(a, b) {
  const closeness = [];
  for (const axis of AXES) {
    if (a.axes[axis] && b.axes[axis]) closeness.push(1 - Math.abs(a.axes[axis] - b.axes[axis]) / 4);
  }
  if (a.strength && b.strength) closeness.push(1 - Math.abs(a.strength - b.strength) / 4);
  const axes = closeness.length ? closeness.reduce((x, y) => x + y, 0) / closeness.length : 0.5;
  return 0.5 * jaccard(a.accords, b.accords) + 0.35 * axes + 0.15 * jaccard(a.traits, b.traits);
}

function scoreProduct(product, profile, catalogue) {
  const parts = {};

  parts.accords = overlap(profile.accords, (key) => hasAccord(product, key), product.accords, 0.8);
  parts.traits = product.traits.size ? overlap(profile.traits, (key) => product.traits.has(key), product.traits, 0.6) : null;
  parts.occasions = product.occasions.size
    ? overlap(profile.occasions, (key) => product.occasions.has(key), product.occasions, 0.7)
    : null;

  // Evidence is a property of the answers, not of the product, so a product
  // missing its spectra still counts as missing them.
  const axisScores = [];
  let axisEvidence = 0;
  for (const axis of AXES) {
    const target = weightedMean(profile.axes[axis]);
    if (!target) continue;
    axisEvidence += target.evidence;
    if (product.axes[axis] == null) continue;
    axisScores.push(1 - Math.abs(product.axes[axis] - target.value) / 4);
  }
  parts.axes = axisScores.length ? axisScores.reduce((x, y) => x + y, 0) / axisScores.length : null;

  const strengthTarget = weightedMean(profile.strength);
  parts.strength = strengthTarget && product.strength ? 1 - Math.abs(product.strength - strengthTarget.value) / 4 : null;

  const ref = profile.reference;
  if (ref?.productId) {
    const target = catalogue.get(ref.productId);
    parts.reference = product.id === ref.productId ? 1 : target ? similarity(product, target) ** 1.2 * 0.9 : null;
  } else if (ref?.brand) {
    parts.reference = ref.brand.some((w) => tokenHit(w, product.brandTokens)) ? 0.8 : 0;
  }

  // Weight axis and strength evidence by how much the answers actually said
  // about them, so a single soft hint cannot outvote the whole profile.
  const weights = { ...WEIGHTS };
  weights.axes *= Math.min(1, axisEvidence / 2) || 0;
  weights.strength *= Math.min(1, (strengthTarget?.evidence || 0) / 1.5);

  let num = 0;
  let den = 0;
  let possible = 0;
  for (const [key, weight] of Object.entries(weights)) {
    if (!weight) continue;
    const askedAbout =
      key === 'reference' ? Boolean(ref) : key === 'axes' || key === 'strength' ? true : Object.keys(profile[key] || {}).length > 0;
    if (!askedAbout) continue;
    possible += weight;
    if (parts[key] == null) continue;
    num += parts[key] * weight;
    den += weight;
  }

  let total = den ? num / den : 0;
  if (possible && den / possible < 0.5) total *= 0.9;

  return { total, parts };
}

function chooseSet(ranked, profile, size = 3) {
  const pool = ranked.filter((r) => r.product.available);
  if (!pool.length) return [];

  const picks = [];
  const referenced = profile.reference?.productId ? pool.find((r) => r.product.id === profile.reference.productId) : null;
  picks.push(referenced || pool[0]);

  const top = picks[0].score.total;
  const floors = [0.9, 0.85];

  while (picks.length < Math.min(size, pool.length)) {
    const floor = top * (floors[picks.length - 1] ?? 0.8);
    const remaining = pool.filter((r) => !picks.includes(r));
    const candidates = remaining.filter((r) => r.score.total >= floor);
    const field = candidates.length ? candidates : remaining.slice(0, 6);

    let best = null;
    let bestValue = -Infinity;
    for (const r of field) {
      const closest = Math.max(...picks.map((p) => similarity(r.product, p.product)));
      const value = r.score.total - 0.18 * closest;
      if (value > bestValue) {
        bestValue = value;
        best = r;
      }
    }
    picks.push(best);
  }

  return picks;
}

/* ========================================================================
   THE ELEMENT
   ======================================================================== */

class LarcinScentFinder extends HTMLElement {
  #config = {};
  #catalogue = new Map();
  #products = [];
  #route = null;
  #steps = [];
  #step = 0;
  #answers = {};
  #results = [];
  #profile = null;
  #busy = false;
  #started = false;
  #notice = '';
  #stickyObserver = null;

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    try {
      this.#config = JSON.parse(this.querySelector('[data-lsf-config]')?.textContent || '{}');
      const raw = JSON.parse(this.querySelector('[data-lsf-products]')?.textContent || '[]');
      this.#products = raw.map(normaliseProduct);
      for (const p of this.#products) this.#catalogue.set(p.id, p);
    } catch (error) {
      console.error('[larcin-scent-finder] could not read product data', error);
      this.#products = [];
    }

    if (!this.#products.some((p) => p.available)) {
      this.#show('empty');
      this.querySelector('[data-lsf-view="landing"]').hidden = true;
      return;
    }

    const next = this.querySelector('[data-lsf-next]');
    const back = this.querySelector('[data-lsf-back]');
    if (next) next.dataset.label = next.textContent.trim();
    if (back) back.dataset.label = back.textContent.trim();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#stickyObserver?.disconnect();
    delete this.dataset.ready;
  }

  /* ------------------------------------------------------------- events */

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const route = target.closest('[data-lsf-route]');
    if (route) return this.#startRoute(route.dataset.lsfRoute);

    if (target.closest('[data-lsf-reset], [data-lsf-restart]')) return this.#reset();
    if (target.closest('[data-lsf-back]')) return this.#go(-1);
    if (target.closest('[data-lsf-next]')) return this.#go(1);

    const option = target.closest('[data-lsf-option]');
    if (option) return this.#choose(option.dataset.lsfOption);

    const known = target.closest('[data-lsf-known]');
    if (known) return this.#chooseKnown(known.dataset.lsfKnown);

    if (target.closest('[data-lsf-add-all]')) return this.#addAll();

    const single = target.closest('[data-lsf-add-one]');
    if (single) return this.#addOne(Number(single.dataset.lsfAddOne), single);
  };

  #onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.matches('[data-lsf-range]')) {
      const id = this.#steps[this.#step];
      const value = Number(input.value);
      if (input.dataset.lsfRange === 'strength') {
        this.#answers[id] = value;
      } else {
        this.#answers[id] = { ...(this.#answers[id] || {}), [input.dataset.lsfRange]: value };
      }
      const label = this.querySelector(`[data-lsf-range-label="${input.dataset.lsfRange}"]`);
      const text = this.#rangeWords(input.dataset.lsfRange, value);
      if (label) label.textContent = text;
      input.setAttribute('aria-valuetext', text);
      this.#syncNext();
    }

    if (input.matches('[data-lsf-known-input]')) {
      const id = this.#steps[this.#step];
      this.#answers[id] = { text: input.value, productId: null, brand: null };
      this.#renderSuggestions(input.value);
      this.querySelectorAll('[data-lsf-known]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      this.querySelector('[data-lsf-known-message]')?.setAttribute('hidden', '');
      this.#syncNext();
    }
  };

  #onKeydown = (event) => {
    if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.matches('[data-lsf-known-input]')) {
      event.preventDefault();
      const first = this.querySelector('[data-lsf-suggestions] [data-lsf-known]');
      if (first) first.click();
      else this.#go(1);
    }
  };

  /* ------------------------------------------------------------- flow */

  #startRoute(route) {
    if (!ROUTES[route]) return;
    this.#route = route;
    this.#steps = ROUTES[route];
    this.#step = 0;
    this.#answers = {};
    this.#notice = '';

    if (!this.#started) {
      this.#started = true;
      this.#track('scent_finder_started');
    }
    this.#track('scent_finder_route_selected');

    this.#show('flow');
    this.#renderQuestion();
  }

  #reset() {
    this.#route = null;
    this.#steps = [];
    this.#step = 0;
    this.#answers = {};
    this.#results = [];
    this.#show('landing');
    this.scrollIntoView({ behavior: this.#motion(), block: 'start' });
    this.querySelector('[data-lsf-route]')?.focus({ preventScroll: true });
  }

  #go(direction) {
    if (direction < 0) {
      if (this.#step === 0) return this.#reset();
      this.#step -= 1;
      return this.#renderQuestion();
    }

    const id = this.#steps[this.#step];
    const question = QUESTIONS[id];

    if (question.type === 'known') {
      const ok = this.#resolveKnown(id, question.optional);
      if (!ok) return;
    }
    if (question.type === 'strength' && this.#answers[id] == null) this.#answers[id] = 3;
    if (question.type === 'scales') {
      this.#answers[id] = { fw: 3, cs: 3, ss: 3, sd: 3, ...(this.#answers[id] || {}) };
    }

    this.#track('scent_finder_question_answered', { question: id, step: this.#step + 1 });

    if (this.#step < this.#steps.length - 1) {
      this.#step += 1;
      this.#renderQuestion();
    } else {
      this.#finish();
    }
  }

  #choose(value) {
    const id = this.#steps[this.#step];
    this.#answers[id] = value;
    for (const el of this.querySelectorAll('[data-lsf-option]')) {
      el.setAttribute('aria-pressed', String(el.dataset.lsfOption === value));
    }
    this.#syncNext();
  }

  #syncNext() {
    const id = this.#steps[this.#step];
    const question = QUESTIONS[id];
    const next = this.querySelector('[data-lsf-next]');
    if (!next) return;

    let ready = true;
    if (question.type === 'options' || question.type === 'scenes') ready = Boolean(this.#answers[id]);
    if (question.type === 'known' && !question.optional) {
      const a = this.#answers[id];
      ready = Boolean(a?.productId || a?.brand || (a?.text || '').trim().length >= 2);
    }
    next.disabled = !ready;
  }

  /* ------------------------------------------------------------- questions */

  #renderQuestion() {
    const id = this.#steps[this.#step];
    const question = QUESTIONS[id];
    const [title, help] = this.#config.text?.questions?.[id] || ['', ''];
    const total = this.#steps.length;
    const answer = this.#answers[id];

    const bar = this.querySelector('[data-lsf-progress]');
    bar?.setAttribute('aria-valuemax', String(total));
    bar?.setAttribute('aria-valuenow', String(this.#step + 1));
    bar?.setAttribute('aria-valuetext', `Question ${this.#step + 1} of ${total}`);
    const fill = this.querySelector('[data-lsf-progress-fill]');
    if (fill) fill.style.inlineSize = `${((this.#step + 1) / total) * 100}%`;
    const count = this.querySelector('[data-lsf-progress-count]');
    if (count) count.textContent = `${this.#step + 1} / ${total}`;

    const next = this.querySelector('[data-lsf-next]');
    if (next) {
      next.textContent = this.#step === total - 1 ? 'See my matches' : next.dataset.label || 'Continue';
    }

    const crumb = this.querySelector('[data-lsf-crumb]');
    if (crumb) crumb.textContent = this.#config.text?.routes?.[this.#route] || '';

    let body = `
      <p class="lsf-question__kicker">${escapeHtml(this.#config.text?.routes?.[this.#route] || '')}</p>
      <h2 class="lsf-question__title" tabindex="-1" data-lsf-question-title>${escapeHtml(title)}</h2>
      ${help ? `<p class="lsf-question__help">${escapeHtml(help)}</p>` : ''}
    `;

    if (question.type === 'options') {
      body += `<div class="lsf-options" role="group" aria-label="${escapeHtml(title)}">${question.options
        .map(
          (o) => `
          <button type="button" class="lsf-option" data-lsf-option="${o.value}" aria-pressed="${answer === o.value}">
            <span class="lsf-option__icon" aria-hidden="true">${o.icon}</span>
            <span class="lsf-option__copy"><strong>${escapeHtml(o.label)}</strong><span>${escapeHtml(o.text)}</span></span>
          </button>`
        )
        .join('')}</div>`;
    }

    if (question.type === 'strength') {
      const value = typeof answer === 'number' ? answer : 3;
      body += `
        <div class="lsf-scales">
          <div class="lsf-scale">
            <div class="lsf-scale__ends" aria-hidden="true">
              <span>${escapeHtml(this.#config.text?.strengthLow || 'Quiet')}</span>
              <span>${escapeHtml(this.#config.text?.strengthHigh || 'Loud')}</span>
            </div>
            <input type="range" min="1" max="5" step="1" value="${value}" data-lsf-range="strength"
              aria-label="${escapeHtml(title)}" aria-valuetext="${STRENGTH_WORDS[value]}">
            <p class="lsf-scale__value" data-lsf-range-label="strength">${STRENGTH_WORDS[value]}</p>
          </div>
        </div>`;
    }

    if (question.type === 'scales') {
      const values = { fw: 3, cs: 3, ss: 3, sd: 3, ...(answer || {}) };
      body += `<div class="lsf-scales">${question.scales
        .map((scale) => {
          const words = this.#rangeWords(scale.axis, values[scale.axis]);
          return `
          <div class="lsf-scale">
            <div class="lsf-scale__ends" aria-hidden="true"><span>${scale.low}</span><span>${scale.high}</span></div>
            <input type="range" min="1" max="5" step="1" value="${values[scale.axis]}" data-lsf-range="${scale.axis}"
              aria-label="${scale.low} to ${scale.high}" aria-valuetext="${words}">
            <p class="lsf-scale__value" data-lsf-range-label="${scale.axis}">${words}</p>
          </div>`;
        })
        .join('')}</div>`;
    }

    if (question.type === 'known') {
      const text = answer?.text || '';
      body += `
        <div class="lsf-known">
          <label class="lsf-visually-hidden" for="lsf-known-${this.id || 'input'}">${escapeHtml(title)}</label>
          <input id="lsf-known-${this.id || 'input'}" type="search" autocomplete="off" spellcheck="false"
            placeholder="Search a fragrance or brand…" value="${escapeHtml(text)}" data-lsf-known-input
            aria-describedby="lsf-known-msg">
          <div class="lsf-known__suggestions" data-lsf-suggestions role="group" aria-label="Matching fragrances"></div>
          <p class="lsf-known__message" id="lsf-known-msg" data-lsf-known-message role="status" hidden></p>
          <div class="lsf-known__quick" role="group" aria-label="Popular choices">
            ${this.#knownPicks()
              .map((p) => this.#knownButton(p, answer?.productId === p.id))
              .join('')}
            ${question.optional ? `<button type="button" class="lsf-chip" data-lsf-known="skip" aria-pressed="${answer === 'skip'}">I don't know / skip</button>` : ''}
          </div>
        </div>`;
    }

    const area = this.querySelector('[data-lsf-question]');
    area.innerHTML = body;

    if (question.type === 'scenes') {
      const template = this.querySelector('template[data-lsf-scenes]');
      if (template) {
        const grid = template.content.firstElementChild.cloneNode(true);
        grid.setAttribute('aria-label', title);
        for (const el of grid.querySelectorAll('[data-lsf-option]')) {
          el.setAttribute('aria-pressed', String(el.dataset.lsfOption === answer));
        }
        area.append(grid);
      }
    }

    if (question.type === 'known' && answer?.text && !answer.productId) this.#renderSuggestions(answer.text);

    const back = this.querySelector('[data-lsf-back]');
    if (back) back.textContent = this.#step === 0 ? 'Change route' : back.dataset.label || 'Back';

    this.#syncNext();

    const heading = area.querySelector('[data-lsf-question-title]');
    heading?.focus({ preventScroll: true });
    if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ behavior: this.#motion(), block: 'start' });
  }

  #rangeWords(axis, value) {
    if (axis === 'strength') return STRENGTH_WORDS[value] || '';
    const scale = QUESTIONS.balance.scales.find((s) => s.axis === axis);
    if (!scale) return '';
    if (value === 1) return `Strongly ${scale.low.toLowerCase()}`;
    if (value === 2) return `Leans ${scale.low.toLowerCase()}`;
    if (value === 3) return 'Balanced';
    if (value === 4) return `Leans ${scale.high.toLowerCase()}`;
    return `Strongly ${scale.high.toLowerCase()}`;
  }

  /* ------------------------------------------------------------- match a fragrance */

  #knownPicks() {
    const chosen = (this.#config.knownPicks || []).map((id) => this.#catalogue.get(id)).filter((p) => p?.inspired);
    return (chosen.length ? chosen : this.#products.filter((p) => p.inspired)).slice(0, 8);
  }

  #knownButton(product, pressed) {
    return `<button type="button" class="lsf-chip" data-lsf-known="${product.id}" aria-pressed="${pressed}">${escapeHtml(product.inspired)}</button>`;
  }

  #search(text) {
    const words = tokens(text);
    if (!words.length) return { products: [], brand: null };

    const scored = [];
    for (const p of this.#products) {
      if (!p.inspired) continue;
      const nameHits = words.filter((w) => tokenHit(w, p.nameTokens)).length;
      const brandHits = words.filter((w) => tokenHit(w, p.brandTokens)).length;
      if (!nameHits && !brandHits) continue;
      // A name match matters more than a brand match, and every word typed
      // should be accounted for by one or the other.
      const covered = words.filter((w) => tokenHit(w, p.nameTokens) || tokenHit(w, p.brandTokens)).length;
      const score = nameHits * 2 + brandHits + (covered === words.length ? 2 : 0);
      scored.push({ p, score, nameHits });
    }
    scored.sort((a, b) => b.score - a.score || a.p.index - b.p.index);

    const named = scored.filter((s) => s.nameHits > 0);
    const brandOnly = !named.length && scored.length ? words.filter((w) => scored.some((s) => tokenHit(w, s.p.brandTokens))) : null;

    return { products: (named.length ? named : scored).slice(0, 6).map((s) => s.p), brand: brandOnly?.length ? brandOnly : null };
  }

  #renderSuggestions(text) {
    const box = this.querySelector('[data-lsf-suggestions]');
    if (!box) return;
    if ((text || '').trim().length < 2) {
      box.innerHTML = '';
      return;
    }
    const { products } = this.#search(text);
    box.innerHTML = products
      .map(
        (p) =>
          `<button type="button" class="lsf-suggestion" data-lsf-known="${p.id}">
            <strong>${escapeHtml(p.inspired)}</strong>${p.brand ? `<span>${escapeHtml(p.brand)}</span>` : ''}
          </button>`
      )
      .join('');
  }

  #chooseKnown(value) {
    const id = this.#steps[this.#step];
    const input = this.querySelector('[data-lsf-known-input]');

    if (value === 'skip') {
      this.#answers[id] = 'skip';
      if (input) input.value = '';
    } else {
      const product = this.#catalogue.get(Number(value));
      if (!product) return;
      this.#answers[id] = { text: product.inspired, productId: product.id, brand: null };
      if (input) input.value = product.inspired;
      const box = this.querySelector('[data-lsf-suggestions]');
      if (box) box.innerHTML = '';
    }

    for (const b of this.querySelectorAll('[data-lsf-known]')) {
      b.setAttribute('aria-pressed', String(b.dataset.lsfKnown === String(value)));
    }
    this.querySelector('[data-lsf-known-message]')?.setAttribute('hidden', '');
    this.#syncNext();
  }

  /** Turns free text into a product or brand before moving on. Returns
   *  false when the step should hold. */
  #resolveKnown(id, optional) {
    const answer = this.#answers[id];
    if (answer === 'skip' || answer?.productId || answer?.brand) return true;

    const text = (answer?.text || '').trim();
    if (!text) {
      if (optional) {
        this.#answers[id] = 'skip';
        return true;
      }
      return false;
    }

    const { products, brand } = this.#search(text);
    const message = this.querySelector('[data-lsf-known-message]');

    if (products.length && !brand) {
      const exact = products[0];
      this.#answers[id] = { text, productId: exact.id, brand: null };
      return true;
    }
    if (brand) {
      this.#answers[id] = { text, productId: null, brand };
      return true;
    }

    if (optional) {
      this.#answers[id] = { text, productId: null, brand: null, unmatched: true };
      return true;
    }

    if (message) {
      message.textContent = 'We couldn’t find that one. Try the name as it is on the bottle, the brand, or pick from the list below.';
      message.hidden = false;
    }
    return false;
  }

  /* ------------------------------------------------------------- results */

  #buildProfile() {
    const profile = emptyProfile();

    for (const id of this.#steps) {
      const question = QUESTIONS[id];
      const answer = this.#answers[id];
      if (answer == null) continue;

      if (question.type === 'options') {
        addSignal(profile, question.options.find((o) => o.value === answer)?.signal, question.weight);
      }
      if (question.type === 'scenes') {
        const lean = this.querySelector(`template[data-lsf-scenes]`)?.content.querySelector(`[data-lsf-option="${answer}"]`)?.dataset.lsfLean;
        addSignal(profile, SCENE_LEANS[lean], question.weight);
      }
      if (question.type === 'strength' && typeof answer === 'number') {
        profile.strength.push([answer, 2]);
      }
      if (question.type === 'scales') {
        for (const axis of AXES) if (typeof answer[axis] === 'number') profile.axes[axis].push([answer[axis], 2]);
      }
      if (question.type === 'known' && answer !== 'skip' && (answer.productId || answer.brand)) {
        profile.reference = { productId: answer.productId, brand: answer.brand, text: answer.text };
      }
    }

    // Naming a fragrance you love also teaches the profile what you like,
    // using that product's own data, so the other two results lean the same
    // way instead of only resembling it.
    const ref = profile.reference?.productId ? this.#catalogue.get(profile.reference.productId) : null;
    if (ref) {
      const w = this.#route === 'known' ? 1 : 0.6;
      for (const key of ref.accords) profile.accords[key] = (profile.accords[key] || 0) + 0.5 * w;
      for (const key of ref.traits) profile.traits[key] = (profile.traits[key] || 0) + 0.5 * w;
      for (const axis of AXES) if (ref.axes[axis]) profile.axes[axis].push([ref.axes[axis], w]);
      if (ref.strength) profile.strength.push([ref.strength, 0.5 * w]);
    }

    return profile;
  }

  #finish() {
    this.#profile = this.#buildProfile();
    this.#rank();
    this.#track('scent_finder_completed', { results: this.#results.map((r) => r.product.handle) });
    this.#renderResults();
    this.#track('scent_finder_result_viewed', {
      results: this.#results.map((r, i) => ({ position: i + 1, handle: r.product.handle, match: this.#percent(r.score.total) })),
    });
  }

  #rank() {
    const ranked = this.#products
      .map((product) => ({ product, score: scoreProduct(product, this.#profile, this.#catalogue) }))
      .sort((a, b) => b.score.total - a.score.total || a.product.index - b.product.index);
    this.#results = chooseSet(ranked, this.#profile);
  }

  /** The match percentage is the score itself, 0 to 100, capped at 98 so it
   *  never claims to be perfect. Nothing is added to flatter it. */
  #percent(total) {
    return Math.max(1, Math.min(98, Math.round(total * 100)));
  }

  #money(cents) {
    const format = this.#config.moneyFormat || '${{amount}}';
    return formatMoney(Math.round(cents), format, this.#config.currency || 'AUD').replace(/\.00(?=\D*$)/, '');
  }

  #renderResults() {
    const results = this.#results;
    if (!results.length) {
      this.#show('empty');
      return;
    }

    const text = this.#config.text || {};
    const count = results.length;
    const fullPrice = results.reduce((sum, r) => sum + r.product.price, 0);
    const percent = count >= 3 ? Number(this.#config.percent) || 0 : 0;
    const bundlePrice = percent ? fullPrice * (1 - percent / 100) : fullPrice;

    // Profile
    const profileWords = this.#profileWords();
    this.querySelector('[data-lsf-profile-text]').innerHTML = this.#profileSentence(profileWords);
    this.querySelector('[data-lsf-profile-tags]').innerHTML = profileWords
      .slice(0, 4)
      .map((w) => `<li>${escapeHtml(capitalise(w))}</li>`)
      .join('');

    const notice = this.querySelector('[data-lsf-notice]');
    notice.hidden = !this.#notice;
    notice.textContent = this.#notice;

    // Bundle
    this.querySelector('[data-lsf-bundle-list]').innerHTML = results
      .map(
        (r, i) => `
        <li class="lsf-bundle__item">
          <span><strong>${i + 1}. ${escapeHtml(r.product.title)}</strong>${
            r.product.inspired ? `<span>Inspired by ${escapeHtml(r.product.inspired)}${r.product.brand ? ` (${escapeHtml(r.product.brand)})` : ''}</span>` : ''
          }</span>
          <b>${this.#money(r.product.price)}</b>
        </li>`
      )
      .join('');

    const label = this.querySelector('[data-lsf-bundle-label]');
    label.textContent =
      count >= 3
        ? (text.bundleLabel || 'Get all 3').replace('[percent]', String(percent))
        : count === 2
          ? text.bundleLabelPair || 'Get both'
          : '';

    const compare = this.querySelector('[data-lsf-bundle-compare]');
    compare.textContent = percent ? this.#money(fullPrice) : '';
    compare.hidden = !percent;
    this.querySelector('[data-lsf-bundle-total]').textContent = this.#money(bundlePrice);

    const fine = this.querySelector('[data-lsf-bundle-fine]');
    if (fine) fine.hidden = !percent;

    const ctaText = (count >= 3 ? text.bundleCta : text.bundleCtaPair) || 'Add to cart · [price]';
    for (const btn of this.querySelectorAll('[data-lsf-add-all]:not([data-lsf-sticky-cta])')) {
      btn.textContent = ctaText.replace('[price]', this.#money(bundlePrice));
      btn.dataset.label = btn.textContent;
      btn.hidden = count < 2;
    }

    // Sticky bar
    this.querySelector('[data-lsf-sticky-total]').textContent = this.#money(bundlePrice);
    this.querySelector('[data-lsf-sticky-note]').textContent =
      count >= 3 ? (text.stickyNote || '').replace('[percent]', String(percent)) : text.stickyNotePair || '';
    const stickyCta = this.querySelector('[data-lsf-sticky-cta]');
    stickyCta.textContent = count >= 3 ? text.stickyCta || 'Add all 3' : 'Add both';
    stickyCta.dataset.label = stickyCta.textContent;

    // Trio
    const trio = this.querySelector('[data-lsf-trio]');
    trio.hidden = count < 2;
    this.querySelector('[data-lsf-trio-grid]').innerHTML = results
      .map((r, i) => {
        const role = this.#role(i, r.product);
        return `<li class="lsf-trio__item"><small>${role.label}</small><strong>${escapeHtml(r.product.title)}</strong><p>${escapeHtml(role.copy)}</p></li>`;
      })
      .join('');

    // Cards
    this.querySelector('[data-lsf-cards]').innerHTML = results.map((r, i) => this.#card(r, i)).join('');

    this.querySelector('[data-lsf-reason]').textContent = this.#reasonText();

    this.#show('results');
    this.#watchSticky();

    const heading = this.querySelector('[data-lsf-result-title]');
    this.scrollIntoView({ behavior: this.#motion(), block: 'start' });
    heading?.focus({ preventScroll: true });
    this.#announce(`Your ${count} matches are ready.`);
  }

  #profileWords() {
    const p = this.#profile;
    const ranked = [
      ...Object.entries(p.traits).map(([k, v]) => [TRAITS[k], v * 1.1]),
      ...Object.entries(p.accords)
        .filter(([k]) => ACCORDS[k])
        .map(([k, v]) => [ACCORDS[k].label, v]),
    ]
      .filter(([word]) => word)
      .sort((a, b) => b[1] - a[1])
      .map(([word]) => word);
    return [...new Set(ranked)].slice(0, 5);
  }

  #profileSentence(words) {
    const names = this.#results.map((r) => `<strong>${escapeHtml(r.product.title)}</strong>`);
    const ref = this.#profile.reference;
    let lead;
    if (ref?.productId && this.#route === 'known') {
      const product = this.#catalogue.get(ref.productId);
      lead = `You named ${escapeHtml(product?.inspired || ref.text)}.`;
    } else if (ref?.brand && this.#route === 'known') {
      const house = this.#results[0]?.product.brand || ref.text;
      return `You named ${escapeHtml(house)}. These are our takes on ${escapeHtml(house)} fragrances, picked so the ${this.#results.length} cover different moods.`;
    } else if (words.length) {
      const p = this.#profile;
      const traits = Object.entries(p.traits).sort((a, b) => b[1] - a[1]).map(([k]) => TRAITS[k]).filter(Boolean).slice(0, 2);
      const accord = Object.entries(p.accords)
        .filter(([k]) => ACCORDS[k])
        .sort((a, b) => b[1] - a[1])
        .map(([k]) => ACCORDS[k].label)[0];
      lead = traits.length
        ? `Your answers leaned ${escapeHtml(joinWords(traits))}${accord ? `, with a pull toward ${escapeHtml(accord)}` : ''}.`
        : `Your answers pulled toward ${escapeHtml(accord)}.`;
    } else {
      lead = 'Here is where your answers landed.';
    }
    if (names.length === 1) return `${lead} ${names[0]} is the closest fit we have in stock.`;
    return `${lead} ${joinWords(names)} fit that best, and together they give you more than one mood to reach for.`;
  }

  #role(index, product) {
    const [first, second] = this.#results.map((r) => r.product);

    if (index === 0) {
      const ref = this.#profile.reference;
      if (ref?.productId === product.id) {
        return { label: 'Your signature', copy: `Our take on ${product.inspired}, the fragrance you named. The closest match there is.` };
      }
      return { label: 'Your signature', copy: `${product.title} is the closest fit to everything you told us, and the easiest place to start.` };
    }

    if (index === 1) {
      let diff = null;
      for (const axis of ['cs', 'ss', 'strength', 'fw', 'sd']) {
        const a = axis === 'strength' ? first.strength : first.axes[axis];
        const b = axis === 'strength' ? product.strength : product.axes[axis];
        if (a == null || b == null || Math.abs(b - a) < 1) continue;
        if (!diff || Math.abs(b - a) > Math.abs(diff.gap)) diff = { axis, gap: b - a };
      }
      const word = diff
        ? diff.axis === 'strength'
          ? diff.gap > 0 ? 'stronger' : 'softer'
          : AXIS_WORDS[diff.axis][diff.gap > 0 ? 1 : 0]
        : null;
      const degree = diff && Math.abs(diff.gap) < 2 ? 'a little ' : '';
      return {
        label: 'Your alternate',
        copy: word
          ? `${product.title} stays close to your taste but runs ${degree}${word} than ${first.title}, so you have a second mood to reach for.`
          : `${product.title} stays close to your taste and gives you a second mood to reach for.`,
      };
    }

    const others = [first, second].filter(Boolean);
    const newAccord = [...product.familyAccords]
      .filter((a) => ACCORDS[a] && a !== 'dark' && others.every((o) => !o.familyAccords.has(a)))
      .sort((a, b) => (this.#profile.accords[b] || 0) - (this.#profile.accords[a] || 0))[0];
    const newOccasion = [...product.occasions].find((o) => others.every((x) => !x.occasions.has(o)));
    const bits = [];
    if (newAccord) bits.push(`brings ${ACCORDS[newAccord].label} into the set`);
    if (newOccasion) bits.push(`covers ${OCCASIONS[newOccasion]}`);
    return {
      label: 'Your range',
      copy: bits.length
        ? `${product.title} ${joinWords(bits)}, so the three do different jobs.`
        : `${product.title} widens the set so the three do different jobs.`,
    };
  }

  #why(product, index = 0) {
    const p = this.#profile;
    const ref = p.reference;

    const matchedTraits = Object.entries(p.traits)
      .filter(([k]) => product.traits.has(k))
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => TRAITS[k])
      .slice(0, 2);

    const matchedAccords = Object.entries(p.accords)
      .filter(([k]) => ACCORDS[k] && product.familyAccords.has(k))
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => ACCORDS[k].label)
      .slice(0, 2);

    const matchedOccasions = Object.entries(p.occasions)
      .filter(([k]) => product.occasions.has(k))
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => OCCASIONS[k])
      .slice(0, 2);

    const notes = product.keyNotes.map((n) => n.toLowerCase());
    const noteLines = [
      (n) => `${product.title} pairs ${n[0]} and ${n[1]} with ${n[2]}.`,
      (n) => `${product.title} opens on ${n[0]}, with ${n[1]} and ${n[2]} underneath.`,
      (n) => `${product.title} brings together ${n[0]}, ${n[1]} and ${n[2]}.`,
    ];
    let first;
    if (product.desc) first = product.desc.replace(/\s*$/, '').replace(/([^.!?])$/, '$1.');
    else if (notes.length >= 3) first = noteLines[index % noteLines.length](notes);
    else if (notes.length) first = `${product.title} is built around ${joinWords(notes)}.`;
    else first = '';

    let second;
    if (ref?.productId === product.id) second = `It is our take on ${product.inspired}, the one you named.`;
    else if (ref?.productId && this.#route === 'known') {
      const named = this.#catalogue.get(ref.productId);
      if (!named) second = '';
      else if (index === 1) second = `Of everything else we make, it has the most in common with ${named.inspired}.`;
      else {
        const shared = [...product.familyAccords].filter((a) => ACCORDS[a] && a !== 'dark' && named.familyAccords.has(a)).map((a) => ACCORDS[a].label)[0];
        second = shared
          ? `It shares the ${shared} in ${named.inspired} and takes it somewhere new.`
          : `It is another close relative of ${named.inspired}, with a different angle.`;
      }
    } else {
      const options = [
        matchedTraits.length && `It matches the ${joinWords(matchedTraits)} feel you picked.`,
        matchedAccords.length && `It leans into the ${joinWords(matchedAccords)} you were drawn to.`,
        matchedOccasions.length && `It suits ${joinWords(matchedOccasions)}, which is where you said you'd wear it.`,
      ];
      // Start from a different kind of evidence on each card, falling back
      // through the others when this product has nothing for it.
      second = [0, 1, 2].map((k) => options[(index + k) % 3]).find(Boolean) || 'It sits closest to your answers on warmth, sweetness and strength.';
    }

    return [first, second].filter(Boolean).join(' ');
  }

  #card(result, index) {
    const p = result.product;
    const text = this.#config.text || {};
    const pct = this.#percent(result.score.total);
    const img = p.image?.src
      ? `<img class="lsf-card__img" src="${escapeHtml(p.image.src)}"
          srcset="${[240, 360, 480, 600, 800]
            .map((w) => `${escapeHtml(p.image.src.replace(/([?&])width=\d+/, `$1width=${w}`))} ${w}w`)
            .join(', ')}"
          sizes="(min-width: 1000px) 330px, (min-width: 750px) 46vw, 126px"
          alt="${escapeHtml(p.image.alt || p.title)}" loading="lazy" decoding="async" width="600" height="612">`
      : `<span class="lsf-card__placeholder" aria-hidden="true">${escapeHtml(p.title.charAt(0))}</span>`;

    const captions = ['Your strongest overall match.', 'A second direction that still fits.', 'Adds range to your rotation.'];

    const buyLabel = escapeHtml((text.individualCta || 'Add 50ml · [price]').replace('[price]', this.#money(p.price)));

    // Layout: image, then name with match score, inspiration, notes, the
    // reason in its own panel, and price, link and button under a rule.
    return `
      <li class="lsf-card">
        <div class="lsf-card__media">
          ${img}
          <span class="lsf-card__rank">Match #${index + 1}</span>
          ${this.#results.length >= 2 ? `<span class="lsf-card__inset">Included in your ${this.#results.length}</span>` : ''}
        </div>
        <div class="lsf-card__body">
          <div class="lsf-card__headline">
            <h3 class="lsf-card__title"><a href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a></h3>
            <span class="lsf-card__match"><b>${pct}%</b> quiz match</span>
          </div>
          ${p.inspired ? `<p class="lsf-card__inspired">Inspired by <strong>${escapeHtml(p.inspired)}</strong>${p.brand ? ` (${escapeHtml(p.brand)})` : ''}</p>` : ''}
          ${p.keyNotes.length ? `<ul class="lsf-card__chips" role="list" aria-label="Key notes">${p.keyNotes.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ul>` : ''}
          <div class="lsf-card__why">
            <strong>Why we picked it</strong>
            <p>${escapeHtml(this.#why(p, index))}</p>
          </div>
          <p class="lsf-visually-hidden">${captions[index] || ''}</p>
          <div class="lsf-card__purchase">
            <div class="lsf-card__row">
              <span class="lsf-card__price">${this.#money(p.price)}</span>
              <a class="lsf-card__view" href="${escapeHtml(p.url)}">${escapeHtml(text.viewCta || 'View fragrance')}</a>
            </div>
            <button type="button" class="lsf-btn lsf-btn--line lsf-card__buy" data-lsf-add-one="${p.id}" data-label="${buyLabel}">
              ${buyLabel}
            </button>
          </div>
        </div>
      </li>`;
  }

  #reasonText() {
    const ref = this.#profile.reference;
    const unmatched = Object.values(this.#answers).find((a) => a?.unmatched);
    let text;
    if (this.#route === 'known') {
      text = ref?.brand
        ? 'You named a brand rather than a fragrance, so these are our takes on that house, ranked by how much they have in common with each other.'
        : 'The fragrance you named was the main signal. We started with our take on it, then chose the two Larcin fragrances that share the most with it in notes, warmth, sweetness and strength.';
    } else if (this.#route === 'taste') {
      text = 'This route weighed your scent direction and final note choice most, then occasion and strength. Every fragrance in the range was scored on the same rules, and the top three were chosen to cover more than one mood.';
    } else {
      text = 'This route combined the impression you want, your scene, occasion, strength, the four balances and your note direction. Note direction and the balances count most. Every fragrance in the range was scored on the same rules.';
    }
    if (unmatched) text += ' We didn’t recognise the fragrance you typed, so it didn’t shape this result.';
    text += ' The match percentage is how closely each one fits your answers on that scoring, not a lab test.';
    return text;
  }

  /* ------------------------------------------------------------- sticky bar */

  #watchSticky() {
    const sticky = this.querySelector('[data-lsf-sticky]');
    const main = this.querySelector('.lsf-bundle [data-lsf-add-all]');
    if (!sticky || !main) return;

    sticky.hidden = this.#results.length < 2;
    this.#stickyObserver?.disconnect();
    if (sticky.hidden || !('IntersectionObserver' in window)) return;

    // Only one "add all" at a time: the bar steps aside while the real
    // button is on screen.
    this.#stickyObserver = new IntersectionObserver(([entry]) => {
      sticky.toggleAttribute('data-away', entry.isIntersecting);
    });
    this.#stickyObserver.observe(main);
  }

  /* ------------------------------------------------------------- cart */

  /** Re-reads each product from Shopify immediately before adding. */
  async #checkAvailability(products) {
    const root = (this.#config.root || '/').replace(/\/?$/, '/');
    const checks = await Promise.all(
      products.map(async (p) => {
        try {
          const res = await fetch(`${root}products/${encodeURIComponent(p.handle)}.js`, { headers: { Accept: 'application/json' } });
          if (!res.ok) return { p, ok: true };
          const data = await res.json();
          const variant = (data.variants || []).find((v) => v.id === p.variant);
          if (variant && typeof variant.price === 'number') p.price = variant.price;
          return { p, ok: Boolean(variant?.available) };
        } catch {
          // If the check itself fails, let Shopify's own add response decide.
          return { p, ok: true };
        }
      })
    );
    return checks.filter((c) => !c.ok).map((c) => c.p);
  }

  #setBusy(buttons, busy, label) {
    for (const b of buttons) {
      b.disabled = busy;
      b.toggleAttribute('aria-busy', busy);
      if (busy) b.textContent = label;
      else if (b.dataset.label) b.textContent = b.dataset.label;
    }
  }

  #status(message, isError = false) {
    const el = this.querySelector('[data-lsf-status]');
    if (!el) return;
    el.textContent = message;
    el.toggleAttribute('data-error', isError);
  }

  async #addAll() {
    if (this.#busy || this.#results.length < 2) return;
    this.#busy = true;
    const buttons = [...this.querySelectorAll('[data-lsf-add-all]')];
    this.#setBusy(buttons, true, 'Adding…');
    this.#status('');

    const products = this.#results.map((r) => r.product);

    try {
      const soldOut = await this.#checkAvailability(products);
      if (soldOut.length) {
        for (const p of soldOut) p.available = false;
        const names = joinWords(soldOut.map((p) => p.title));
        this.#rank();
        const swapped = joinWords(this.#results.map((r) => r.product).filter((p) => !products.includes(p)).map((p) => p.title));
        this.#notice = swapped
          ? `${names} sold out a moment ago, so we swapped in ${swapped}. Check the new set, then add it.`
          : `${names} sold out a moment ago. Here is the best set we have in stock.`;
        this.#busy = false;
        this.#renderResults();
        this.#announce(this.#notice);
        return;
      }

      const ok = await this.#postToCart(
        products.map((p) => ({ id: p.variant, quantity: 1 })),
        'larcin-scent-finder-all'
      );
      if (ok) {
        this.#status('Added. Your cart is open.');
        this.#track('scent_finder_all_3_added', {
          count: products.length,
          products: products.map((p) => p.handle),
          value: products.reduce((sum, p) => sum + p.price, 0) / 100,
        });
      }
    } finally {
      this.#busy = false;
      this.#setBusy(buttons, false);
    }
  }

  async #addOne(productId, button) {
    if (this.#busy) return;
    const product = this.#catalogue.get(productId);
    if (!product) return;

    this.#busy = true;
    this.#setBusy([button], true, 'Adding…');

    try {
      const soldOut = await this.#checkAvailability([product]);
      if (soldOut.length) {
        product.available = false;
        button.dataset.label = 'Sold out';
        this.#status(`${product.title} just sold out.`, true);
        return;
      }
      const ok = await this.#postToCart([{ id: product.variant, quantity: 1 }], 'larcin-scent-finder-single');
      if (ok) {
        const position = this.#results.findIndex((r) => r.product.id === productId) + 1;
        this.#track('scent_finder_individual_added', { product: product.handle, position, value: product.price / 100 });
      }
    } finally {
      this.#busy = false;
      this.#setBusy([button], false);
      if (!product.available) button.disabled = true;
    }
  }

  /**
   * One request, however many lines. Follows the same pattern as the bundle
   * builder: announce the add, post it, resolve with the real cart. The
   * LARCIN drawer re-renders and opens on the successful write.
   */
  async #postToCart(items, context) {
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context,
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

    try {
      const res = await fetch(this.#config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await res.json().catch(() => ({}));
      const cart = await this.#fetchCart();

      if (!res.ok || data.status) {
        const message = data.description || data.message || 'We couldn’t add that. Please try again.';
        this.#status(message, true);
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items || [], source: context, itemCount: 0 },
        });
        return false;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items || [], source: context, itemCount: items.length, sections: data.sections },
      });
      return true;
    } catch (error) {
      console.error('[larcin-scent-finder] cart error', error);
      this.#status('We couldn’t reach the cart. Please try again.', true);
      deferred.reject?.(error);
      return false;
    }
  }

  async #fetchCart() {
    try {
      const res = await fetch(this.#config.cartUrl || '/cart.js', { headers: { Accept: 'application/json' } });
      return await res.json();
    } catch {
      return { items: [] };
    }
  }

  /* ------------------------------------------------------------- plumbing */

  #show(view) {
    this.dataset.state = view;
    for (const el of this.querySelectorAll('[data-lsf-view]')) el.hidden = el.dataset.lsfView !== view;
    const inQuiz = view !== 'landing';
    this.querySelector('[data-lsf-reset]').hidden = !inQuiz;
    this.querySelector('[data-lsf-crumb]').hidden = view !== 'flow';
    if (view !== 'results') {
      this.#stickyObserver?.disconnect();
      const sticky = this.querySelector('[data-lsf-sticky]');
      if (sticky) sticky.hidden = true;
    }
  }

  #announce(message) {
    const el = this.querySelector('[data-lsf-announce]');
    if (!el) return;
    el.textContent = '';
    requestAnimationFrame(() => (el.textContent = message));
  }

  #motion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * Analytics. Three ways to listen, none of them tied to a vendor:
   *   window.dataLayer            for GTM
   *   'larcin:scent-finder'       a DOM event on document, detail.event names it
   *   Shopify.analytics.publish   for a custom pixel in Customer events
   * No free text and nothing personal is ever sent.
   */
  #track(event, data = {}) {
    const detail = { event, route: this.#route, ...data };
    try {
      (window.dataLayer = window.dataLayer || []).push(detail);
    } catch {
      /* noop */
    }
    document.dispatchEvent(new CustomEvent('larcin:scent-finder', { detail }));
    try {
      window.Shopify?.analytics?.publish?.(event, detail);
    } catch {
      /* noop */
    }
  }
}

if (!customElements.get('larcin-scent-finder')) {
  customElements.define('larcin-scent-finder', LarcinScentFinder);
}

export { normaliseProduct, scoreProduct, chooseSet, emptyProfile, addSignal, QUESTIONS, SCENE_LEANS };
